import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery as useConvexQuery, useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { Id } from 'convex/_generated/dataModel'
import React, { useEffect, useMemo, useState } from 'react'
import { BookOpen, Lock, Plus, Edit2 } from 'lucide-react'

export const Route = createFileRoute('/app/decks/')({
  component: DecksPage,
})

function DecksPage() {
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to view decks</div>
      </div>
    )
  }

  return <DecksContent userId={currentUser._id} />
}

function DecksContent({ userId }: { userId: Id<'users'> }) {
  const ensureDefaultDeck = useMutation(api.flashcards.ensureDefaultDeck)

  // Fetch all decks (user + system) with flashcard counts
  const decks = useConvexQuery(api.flashcards.getAllDecksWithCounts, { userId })

  // Ensure user has a default deck on component mount
  useEffect(() => {
    ensureDefaultDeck({ userId }).catch((error) => {
      console.error('Failed to ensure default deck:', error)
    })
  }, [userId, ensureDefaultDeck])

  if (decks === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading decks...</div>
      </div>
    )
  }

  // Separate system and user decks
  const systemDecks = decks.filter((deck) => deck.isSystem)
  const userDecks = decks.filter((deck) => !deck.isSystem)

  return (
    <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-8 terminal-border p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
                [FLASHCARD DECKS]
              </h1>
              <p className="text-trail-tan font-mono text-sm sm:text-base">
                System collections & your personal decks
              </p>
            </div>
            <Link
              to="/app/decks/add"
              className="px-4 sm:px-6 py-2 sm:py-3 bg-terminal-green text-black font-bold font-mono text-sm sm:text-base border-4 border-terminal-green hover:bg-black hover:text-terminal-green transition-all retro-glow"
            >
              + CREATE DECK
            </Link>
          </div>
        </div>

        {/* System Decks Section */}
        {systemDecks.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-terminal-green" />
              <h2 className="text-xl font-bold text-terminal-green font-mono tracking-wider">
                [SYSTEM DECKS]
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {systemDecks.map((deck) => (
                <DeckCard key={deck._id} deck={deck} isSystem={true} userId={userId} />
              ))}
            </div>
          </div>
        )}

        {/* User Decks Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-sky-blue" />
            <h2 className="text-xl font-bold text-sky-blue font-mono tracking-wider">
              [MY DECKS]
            </h2>
          </div>
          {userDecks.length === 0 ? (
            <div className="terminal-border p-8 text-center">
              <div className="text-sky-blue text-xl font-bold font-mono mb-3">
                NO PERSONAL DECKS YET
              </div>
              <p className="text-trail-tan font-mono mb-6">
                Create your first deck to get started!
              </p>
              <Link
                to="/app/decks/add"
                className="inline-block px-6 py-3 bg-terminal-green text-black font-bold font-mono border-4 border-terminal-green hover:bg-black hover:text-terminal-green transition-all"
              >
                CREATE FIRST DECK
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {userDecks.map((deck) => (
                <DeckCard key={deck._id} deck={deck} isSystem={false} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function DeckCard({
  deck,
  isSystem,
  userId,
}: {
  deck: {
    _id: Id<'flashcardDecks'>
    name: string
    description?: string
    createdAt: number
    flashcardCount: number
    status?: 'active' | 'paused'
    color?: string
  }
  isSystem: boolean
  userId?: Id<'users'>
}) {
  const navigate = useNavigate()
  const [isAddingDeck, setIsAddingDeck] = useState(false)
  const copySystemDeck = useMutation(api.flashcards.copySystemDeckToUser)

  const borderColor = isSystem ? 'border-terminal-green' : 'border-sky-blue'
  const titleColor = isSystem ? 'text-terminal-green' : 'text-sky-blue'
  const badgeColor = isSystem
    ? 'bg-terminal-green/10 border-terminal-green'
    : 'bg-sky-blue/10 border-sky-blue'
  const badgeText = isSystem ? 'SYSTEM' : 'PERSONAL'

  const isActive = (deck.status ?? 'active') === 'active'

  // Handle adding system deck to user's collection
  const handleAddSystemDeck = async (e: React.MouseEvent) => {
    e.preventDefault() // Prevent link navigation
    if (!isSystem || !userId) return

    setIsAddingDeck(true)
    try {
      const result = await copySystemDeck({
        userId,
        systemDeckId: deck._id,
      })

      if (result.created) {
        navigate({ to: `/app/decks/${result.deckId}` })
      } else {
        navigate({ to: `/app/decks/${result.deckId}` })
      }
    } catch (error) {
      console.error('Error adding system deck:', error)
      alert(error instanceof Error ? error.message : 'Failed to add deck to your collection')
    } finally {
      setIsAddingDeck(false)
    }
  }

  // Memoized smart default color logic - prevents recalculation on every render
  const deckColor = useMemo(() => {
    if (deck.color) return deck.color

    const name = deck.name.toLowerCase()
    if (isSystem) {
      if (name.includes('verb')) return '#10b981'
      if (name.includes('vocabulary')) return '#3b82f6'
      if (name.includes('grammar')) return '#8b5cf6'
      if (name.includes('phrase')) return '#f59e0b'
      return '#0ea5e9'
    }

    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    const colors = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899']
    return colors[hash % colors.length]
  }, [deck.color, deck.name, isSystem])

  return (
    <Link
      to="/app/decks/$deckId"
      params={{ deckId: deck._id }}
      className={`terminal-border ${borderColor} p-4 sm:p-6 bg-black hover:border-terminal-green transition-all cursor-pointer block`}
    >
      {/* Color Indicator */}
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-4 h-4 border-2 border-current flex-shrink-0"
          style={{ backgroundColor: deckColor }}
          title="Deck color"
        />
        <div className="flex-1 min-w-0">
          <h2 className={`text-xl font-bold ${titleColor} font-mono break-words`}>
            {deck.name}
          </h2>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {/* Status Badge */}
          <span
            className={`text-xs font-mono px-2 py-1 border-2 whitespace-nowrap ${
              isActive
                ? 'bg-terminal-green/10 border-terminal-green text-terminal-green'
                : 'bg-trail-tan/10 border-trail-tan text-trail-tan'
            }`}
          >
            {isActive ? 'ACTIVE' : 'PAUSED'}
          </span>
          {/* System/Personal Badge */}
          <span
            className={`text-xs font-mono px-2 py-1 border-2 ${badgeColor} ${titleColor} whitespace-nowrap`}
          >
            {badgeText}
          </span>
        </div>
      </div>

      {deck.description && (
        <p className="text-trail-tan text-sm font-mono mb-4 line-clamp-2">{deck.description}</p>
      )}

      {/* Add to My Decks Button (System Decks Only) */}
      {isSystem && userId && (
        <button
          onClick={handleAddSystemDeck}
          disabled={isAddingDeck}
          className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-2 sm:py-2.5 text-sm sm:text-lg font-bold border-2 border-terminal-green text-terminal-green hover:bg-terminal-green/10 font-mono transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isAddingDeck ? (
            <>
              <div className="w-4 h-4 border-2 border-terminal-green border-t-transparent rounded-full animate-spin" />
              <span>ADDING...</span>
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">ADD TO MY DECKS</span>
              <span className="sm:hidden">ADD</span>
            </>
          )}
        </button>
      )}

      {/* Edit Button (User Decks Only) */}
      {!isSystem && (
        <button
          onClick={(e) => {
            e.preventDefault() // Prevent parent Link navigation
            navigate({ to: '/app/decks/$deckId', params: { deckId: deck._id } })
          }}
          className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-2 sm:py-2.5 text-sm sm:text-lg font-bold border-2 border-sky-blue text-sky-blue hover:bg-sky-blue/10 font-mono transition-all"
        >
          <Edit2 className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">EDIT DECK</span>
          <span className="sm:hidden">EDIT</span>
        </button>
      )}

      <div className="flex items-center justify-between text-xs font-mono">
        <span className="text-trail-tan">
          {deck.flashcardCount} card{deck.flashcardCount !== 1 ? 's' : ''}
        </span>
        <span className="text-sky-blue">{new Date(deck.createdAt).toLocaleDateString()}</span>
      </div>
    </Link>
  )
}

